/**
 * WHAT: Privilege level constants, lookup helpers, and badge-styling functions for the 7-tier access model.
 * WHY:  DAMA classifies every database account into a privilege tier (READ through SUPER_ADMIN, plus NONE).
 *       These levels drive attestation risk indicators, privilege mapping UIs, and compliance reports.
 *       Centralizing the definitions here ensures colors, icons, and sort order are consistent everywhere.
 */

/**
 * WHAT: Ordered array of privilege level definitions with value, name, color, and icon.
 * WHY:  Drives badge rendering, dropdown options, and sort order in attestation UIs and
 *       privilege mapping screens across all database technologies.
 */
export const PRIVILEGE_LEVELS = [
  { value: 6, name: 'SUPER_ADMIN', color: '#dc3545', bgColor: '#f8d7da', icon: 'fa-crown' },
  { value: 5, name: 'ADMIN', color: '#fd7e14', bgColor: '#ffe5d0', icon: 'fa-user-shield' },
  { value: 4, name: 'WRITE_ELEVATED', color: '#ffc107', bgColor: '#fff3cd', icon: 'fa-edit' },
  { value: 3, name: 'WRITE', color: '#28a745', bgColor: '#d4edda', icon: 'fa-edit' },
  { value: 2, name: 'READ_ELEVATED', color: '#0dcaf0', bgColor: '#cff4fc', icon: 'fa-eye' },
  { value: 1, name: 'READ', color: '#198754', bgColor: '#d1e7dd', icon: 'fa-eye' },
  { value: -1, name: 'NONE', color: '#6c757d', bgColor: '#e9ecef', icon: 'fa-ban' }
];

/**
 * WHAT: Looks up a privilege level config object by its name string.
 * WHY:  Backend responses include the level name (e.g., "SUPER_ADMIN"); components need the
 *       corresponding color and icon for badge rendering.
 * @param {string} levelName - The privilege level name (e.g., 'SUPER_ADMIN', 'WRITE')
 * @returns {object|null} The privilege level configuration or null if not found
 */
export function getPrivilegeLevelByName(levelName) {
  return PRIVILEGE_LEVELS.find(level => level.name === levelName) || null;
}

/**
 * WHAT: Looks up a privilege level config object by its numeric value.
 * WHY:  Some backend fields store privilege as an integer; this resolves it to the full
 *       config including name, color, and icon for display.
 * @param {number} levelValue - The privilege level value (1-6, -1 for NONE)
 * @returns {object|null} The privilege level configuration or null if not found
 */
export function getPrivilegeLevelByValue(levelValue) {
  return PRIVILEGE_LEVELS.find(level => level.value === levelValue) || null;
}

/**
 * WHAT: Name-to-numeric-value map for sorting privilege levels (higher = more privileged).
 * WHY:  Tables and reports sort users by privilege severity to surface high-risk accounts first.
 */
export const PRIVILEGE_LEVEL_ORDER = {
  'SUPER_ADMIN': 6,
  'ADMIN': 5,
  'WRITE_ELEVATED': 4,
  'WRITE': 3,
  'READ_ELEVATED': 2,
  'READ': 1,
  'NONE': -1
};

/**
 * WHAT: Returns an array of privilege level name strings (excluding NONE).
 * WHY:  Used to populate dropdown options in privilege mapping forms where NONE is not a valid selection.
 */
export function getPrivilegeLevelNames() {
  return PRIVILEGE_LEVELS.filter(l => l.value >= 0).map(l => l.name);
}

/**
 * WHAT: Returns an inline style object for rendering a colored privilege-level badge.
 * WHY:  Attestation tables and access reports display privilege levels as colored badges;
 *       this centralizes the color mapping so all components render them identically.
 * @param {string} levelName - The privilege level name
 * @returns {object} Style object for the badge
 */
export function getPrivilegeLevelStyle(levelName) {
  const level = getPrivilegeLevelByName(levelName);
  if (!level) {
    return {
      background: '#6c757d',
      color: '#fff'
    };
  }
  return {
    background: level.color,
    color: '#fff'
  };
}
